/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

// Pure decision helper for the drive-sharing location-escalation warning.
// Extracted from ActivityContext.setSharingEnabled so it can be unit-tested
// without mounting the Firebase/Capacitor-coupled provider (CLAUDE.md DI rule).
//
// Background: DriveTelemetry background trip capture requires Always location
// (CMMotionActivity fuses with location; SLC + allowsBackgroundLocationUpdates
// only deliver under Always). On "While Using" a trip driven with the app
// closed is never classified automotive → startTrip() never fires → the rating
// freezes silently. When drive-sharing is enabled we escalate to Always, and if
// the outcome isn't Always we log so the failure mode is diagnosable — WITHOUT
// blocking the opt-in (the pref reflects intent regardless).

import type {
  LocationAlwaysOutcome,
  LocationAlwaysStatus,
} from "@/utils/locationAlways";

/**
 * True when a location outcome should trigger the "background capture won't
 * work" warning. Always (works) and unsupported (web/Android, feature inert)
 * are NOT warned; every other status (when_in_use / denied / restricted /
 * not_determined / unknown) IS — background trip capture cannot work there.
 */
export function shouldWarnAboutLocation(
  outcome: LocationAlwaysOutcome,
): boolean {
  return (
    outcome.status !== "authorized_always" && outcome.status !== "unsupported"
  );
}

/** The human-readable warning line (also used as the console.warn message). */
export function locationEscalationWarning(
  outcome: LocationAlwaysOutcome,
): string {
  const needsSettings =
    "needsSettings" in outcome && outcome.needsSettings === true;
  return (
    `[ActivityContext] drive-sharing enabled but location is "${outcome.status}" (not Always)` +
    `${needsSettings ? " — user must change it in Settings" : ""}. ` +
    "Background trip capture will not work until location is Always."
  );
}

/**
 * Whether to show the in-app "set Location to Always" nudge on the drive-
 * sharing settings row. True only when ALL hold:
 *  - drive/activity sharing is ON (otherwise capture isn't expected to run), and
 *  - we're on a platform where the status is meaningful (NOT "unsupported" —
 *    web/Android, where the whole feature is inert), and
 *  - location is NOT already Always (Always = capture works, nothing to nudge).
 *
 * "not_determined" also nudges: the escalation prompt may not have resolved to
 * Always yet (iOS only offers the Always upgrade after a WhenInUse grant +
 * app re-entry), so the user still needs to act. A null/undefined status (not
 * yet checked) does NOT nudge — we don't warn until we actually know.
 */
export function shouldShowAlwaysNudge(
  sharingEnabled: boolean,
  status: LocationAlwaysStatus | "unsupported" | null | undefined,
): boolean {
  if (!sharingEnabled) return false;
  if (status == null) return false;
  if (status === "unsupported") return false;
  return status !== "authorized_always";
}
